import AlignCenterIcon from './icons/AlignCenterIcon.svelte'
import AlignJustifiedIcon from './icons/AlignJustifiedIcon.svelte'
import AlignLeftIcon from './icons/AlignLeftIcon.svelte'
import AlignRightIcon from './icons/AlignRightIcon.svelte'
import BoldIcon from './icons/BoldIcon.svelte'
import H1Icon from './icons/H1Icon.svelte'
import H2Icon from './icons/H2Icon.svelte'
import H3Icon from './icons/H3Icon.svelte'
import H4Icon from './icons/H4Icon.svelte'
import ItalicIcon from './icons/ItalicIcon.svelte'
import LinkIcon from './icons/LinkIcon.svelte'
import ListIcon from './icons/ListIcon.svelte'
import ListNumbersIcon from './icons/ListNumbersIcon.svelte'
import PhotoIcon from './icons/PhotoIcon.svelte'
import StrikethroughIcon from './icons/StrikethroughIcon.svelte'
import UnderlineIcon from './icons/UnderlineIcon.svelte'
import type { MessageFormatter } from '@repo/i18n'
import type { Editor } from '@tiptap/core'
import type { Component } from 'svelte'

interface TextEditorAction {
	icon: Component
	label: string
	action: (editor: Editor) => void
	isActive: (editor: Editor) => boolean
}
export function createTextEditorActions($t: MessageFormatter): Record<string, TextEditorAction[]> {
	return {
		textFormatting: [
			{
				icon: BoldIcon,
				label: $t('editor.actionBold'),
				action: (editor) => editor.chain().focus().toggleBold().run(),
				isActive: (editor) => editor.isActive('bold'),
			},
			{
				icon: ItalicIcon,
				label: $t('editor.actionItalic'),
				action: (editor) => editor.chain().focus().toggleItalic().run(),
				isActive: (editor) => editor.isActive('italic'),
			},
			{
				icon: UnderlineIcon,
				label: $t('editor.actionUnderline'),
				action: (editor) => editor.chain().focus().toggleUnderline().run(),
				isActive: (editor) => editor.isActive('underline'),
			},
			{
				icon: StrikethroughIcon,
				label: $t('editor.actionStrikethrough'),
				action: (editor) => editor.chain().focus().toggleStrike().run(),
				isActive: (editor) => editor.isActive('strike'),
			},
		],
		headings: [
			{
				icon: H1Icon,
				label: $t('editor.actionH1'),
				action: (editor) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
				isActive: (editor) => editor.isActive('heading', { level: 1 }),
			},
			{
				icon: H2Icon,
				label: $t('editor.actionH2'),
				action: (editor) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
				isActive: (editor) => editor.isActive('heading', { level: 2 }),
			},
			{
				icon: H3Icon,
				label: $t('editor.actionH3'),
				action: (editor) => editor.chain().focus().toggleHeading({ level: 3 }).run(),
				isActive: (editor) => editor.isActive('heading', { level: 3 }),
			},
			{
				icon: H4Icon,
				label: $t('editor.actionH4'),
				action: (editor) => editor.chain().focus().toggleHeading({ level: 4 }).run(),
				isActive: (editor) => editor.isActive('heading', { level: 4 }),
			},
		],
		alignment: [
			{
				icon: AlignLeftIcon,
				label: $t('editor.actionAlignLeft'),
				action: (editor) => editor.chain().focus().setTextAlign('left').run(),
				isActive: (editor) => editor.isActive({ textAlign: 'left' }),
			},
			{
				icon: AlignCenterIcon,
				label: $t('editor.actionAlignCenter'),
				action: (editor) => editor.chain().focus().setTextAlign('center').run(),
				isActive: (editor) => editor.isActive({ textAlign: 'center' }),
			},
			{
				icon: AlignRightIcon,
				label: $t('editor.actionAlignRight'),
				action: (editor) => editor.chain().focus().setTextAlign('right').run(),
				isActive: (editor) => editor.isActive({ textAlign: 'right' }),
			},
			{
				icon: AlignJustifiedIcon,
				label: $t('editor.actionAlignJustified'),
				action: (editor) => editor.chain().focus().setTextAlign('justify').run(),
				isActive: (editor) => editor.isActive({ textAlign: 'justify' }),
			},
		],
		listAndIndent: [
			{
				icon: ListIcon,
				label: $t('editor.actionList'),
				action: (editor) => editor.chain().focus().toggleBulletList().run(),
				isActive: (editor) => editor.isActive('bulletList'),
			},
			{
				icon: ListNumbersIcon,
				label: $t('editor.actionListNumbers'),
				action: (editor) => editor.chain().focus().toggleOrderedList().run(),
				isActive: (editor) => editor.isActive('orderedList'),
			},
		],
		linksAndImages: [
			{
				icon: LinkIcon,
				label: $t('editor.actionLink'),
				action: (editor) => {
					const previousUrl = editor.getAttributes('link').href
					const url = window.prompt($t('editor.linkUrl'), previousUrl)
					if (url === null) {
						return
					}

					if (url === '') {
						editor.chain().focus().extendMarkRange('link').unsetLink().run()

						return
					}

					editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
				},
				isActive: (editor) => editor.isActive('link'),
			},
			{
				icon: PhotoIcon,
				label: $t('editor.actionPhoto'),
				action: (editor) => {
					const url = window.prompt($t('editor.imageUrl'))

					if (url) {
						editor.chain().focus().setImage({ src: url }).run()
					}
				},
				isActive: () => false,
			},
		],
	}
}
